import getOffsetXByDirection from "../../utils/getOffsetXByDirection";
import { throttle } from "../../utils/throttle";
import UIComponent from "../component";

export class AudioPlayer extends UIComponent {
    PLAY_STATES = {
        PLAY: 'play',
        PAUSE: 'pause',
    };
    playState = this.PLAY_STATES.PLAY;

    isPointerDown = false;
    raf = null;

    init() {
        this.duration = Number(this.limited || this.duration);
        this.throttleShowPreTime = throttle(this.showTimeByMove.bind(this), 16);
        this.boundHidePreTime = this.hideTimeByMove.bind(this);

        this.playButton.on('click', this.togglePlayByClick.bind(this));
        this.audioEl.on('ended', this.handleEndAudio.bind(this));

        this.volumeSlider.on('input', this.showRangeProgress.bind(this));
        this.volumeSlider.on('input', this.changeVolume.bind(this));

        this.initEventsForPretime();

        this.progressBar.on('pointerdown', this.handleProgressByDown.bind(this));
        window.addEventListener('pointermove', this.changeProgressByMove.bind(this));

        if (this.audioEl.prop('readyState') > 0) {
            this.displayDuration();
        } else {
            this.audioEl.on('loadedmetadata', () => {
                this.displayDuration();
            })
        }

        this.forward?.on('click', this.rewindNext.bind(this));
        this.rewind?.on('click', this.rewindPrev.bind(this));
        this.dropdownSpeed.onChange(this.changeSpeed.bind(this));
        window.addEventListener('keydown', this.handleKeydown.bind(this));

        if (this.limited) {
            this.audioEl.on('timeupdate', this.stopAudioByLimited.bind(this));
        }
    }

    initEventsForPretime() {
        this.progressBar.element.addEventListener('pointermove', this.throttleShowPreTime);
        this.progressBar.element.addEventListener('pointerleave', this.boundHidePreTime);
    }

    destroyEventsForPretime() {
        this.progressBar.element.removeEventListener('pointermove', this.throttleShowPreTime);
        this.progressBar.element.removeEventListener('pointerleave', this.boundHidePreTime);
    }

    togglePlayByClick() {
        if (this.audioEndCheck()) {
            this.changeCurrentTime(0);
            this.onEnded?.();
        }

        this.playState === this.PLAY_STATES.PLAY ? this.play() : this.pause();
    }

    stopAudioByLimited() {
        if (this.audioEndCheck()) {
            this.pause();
            this.onEnded?.();
        }
    }

    handleProgressByDown(ev) {
        ev.preventDefault();

        this.destroyEventsForPretime();

        this.isPointerDown = true;
        const offsetXByDir = getOffsetXByDirection(ev, this.progressBar.element);

        if (this.playState === this.PLAY_STATES.PAUSE) {
            this.beforePlayState = this.playState;
            this.pause();
        }

        this.changeProgressByOffset(offsetXByDir);
        this.showPointer();

        window.addEventListener('pointerup', this.handleProgressByUp.bind(this), { once: true });
    }

    changeProgressByMove(ev) {
        if (!this.isPointerDown) return;
        const offsetXByDir = getOffsetXByDirection(ev, this.progressBar.element);

        this.showTimeByMove(ev);
        this.changeProgressByOffset(offsetXByDir);
    }

    handleProgressByUp(ev) {
        if (!this.isPointerDown) return;
        this.isPointerDown = false;
        this.changeCurrentTime(
            (this.getOffsetInPercent(getOffsetXByDirection(ev, this.progressBar.element)) / 100) * this.duration
        );

        if (!this.audioEndCheck() && this.beforePlayState === this.PLAY_STATES.PAUSE) {
            this.play();
        }

        this.beforePlayState = null;
        this.hidePointer();

        this.initEventsForPretime();
        this.boundHidePreTime();
    }

    changeProgressByOffset(offset) {
        const offsetInPercent = this.getOffsetInPercent(offset);
        this.setSeekBeforeWidth(offsetInPercent);

        const newTime = (offsetInPercent / 100) * this.duration;

        this.onChangeProgress(newTime * 1000);
    }

    /**
     * Изменяет визуальное положение каретки на прогрессбаре
     * Обязательно дополнительно использовать функцию для изменения текущего времени
     * 
     * @param {number} time - время в секундах 
     */
    changeProgressByTime(time) {
        const percent = (time / this.duration) * 100;
        this.setSeekBeforeWidth(percent);
    }

    getOffsetInPercent(offset) {
        let percent;
        const offsetWidth = this.progressBar.prop('offsetWidth');

        if (offsetWidth < offset) percent = 100;
        else if (offset < 0) percent = 0;
        else percent = (offset / offsetWidth) * 100;

        return percent;
    }

    showTimeByMove(ev) {
        const offsetXByDir = getOffsetXByDirection(ev, this.progressBar.element);
        const offsetInPercent = this.getOffsetInPercent(offsetXByDir);

        const time = `"${this.calculateTime((offsetInPercent / 100) * this.duration)}"`;

        this.css('--pre-time-width', offsetInPercent + '%');
        this.css('--pre-time-value', time);
        this.preSelectTime.addClass('visible');
    };

    hideTimeByMove() {
        this.css('--pre-time-width', "0%");
        this.css('--pre-time-value', '"00:00"');
        this.preSelectTime.removeClass('visible');
    }

    handleKeydown(ev) {
        if (document.activeElement !== null && document.activeElement !== document.body) {
            return;
        }

        if (ev.code === 'ArrowLeft') {
            this.rewindPrev();
            return;
        }
        if (ev.code === 'ArrowRight') {
            this.rewindNext();
            return;
        }
        if (ev.code === 'Space') {
            ev.preventDefault();
            this.playState === this.PLAY_STATES.PLAY ? this.play() : this.pause();
            return;
        }
    }

    changeSpeed(num) {
        if (num < 0) {
            console.error('Speed value is incorrect');
            return;
        }

        this.audioEl.element.playbackRate = +num;
    }

    rewindPrev() {
        const time = +this.rewind.attr('data-value') / 1000;
        if (this.getCurrentTime() <= 0) return;
        
        const newTime = this.getCurrentTime() - time;
        this.changeProgressByTime(newTime);
        this.changeCurrentTime(newTime);
        this.onRewinded?.(this.getAudioTimeInMls());
    }

    rewindNext() {
        if (this.audioEndCheck()) return;

        const time = +this.forward.attr('data-value') / 1000;
        const newTime = this.getCurrentTime() + time;
        this.changeProgressByTime(newTime);
        this.changeCurrentTime(newTime);
        this.onRewinded?.(this.getAudioTimeInMls());
    }

    handleEndAudio() {
        this.pause();
        this.onEnded?.();
    }

    play() {
        if (!this.audioEl.prop('paused') && !this.audioEl.prop('ended')) return;

        this.playButton.addClass('ap-pause');
        this.audioEl.element.play();
        requestAnimationFrame(this.whilePlaying.bind(this));
        this.playState = this.PLAY_STATES.PAUSE;
    }

    pause() {
        this.playButton.removeClass('ap-pause');
        this.audioEl.element.pause();
        cancelAnimationFrame(this.raf);
        this.playState = this.PLAY_STATES.PLAY;
    }

    changeVolume({ target }) {
        const soundLevel = target.value / 100;

        this.changeIconByLevel(soundLevel);
        this.audioEl.element.volume = soundLevel;
    }

    changeIconByLevel(num) {
        const cn = 'sound-off';
        num <= 0 ? this.volumeButton.addClass(cn) : this.volumeButton.removeClass(cn);
    }

    getAudioTimeInMls() {
        return this.getCurrentTime() * 1000;
    }

    whilePlaying() {
        this.onProgress?.(this.getAudioTimeInMls());
        const time = Math.floor(this.getCurrentTime());

        this.currentTimeLabel.html(this.calculateTime(time));
        this.changeProgressByTime(time);
        
        this.raf = requestAnimationFrame(this.whilePlaying.bind(this));
    }

    calculateTime(secs) {
        const minutes = Math.floor(secs / 60);
        const seconds = Math.floor(secs % 60);
        const returnedSeconds = seconds < 10 ? '0' + seconds : seconds;
        const returnedMinutes = minutes < 10 ? '0' + minutes : minutes;

        return returnedMinutes + ':' + returnedSeconds;
    }

    showRangeProgress({ target }) {
        this.css(
            '--volume-before-width',
            ((target.value / target.max) * 100) + '%',
        );
    };

    resetprogressBar() {
        this.currentTimeLabel.html(this.calculateTime(0));
        this.setSeekBeforeWidth(0);
    }

    displayDuration() {
        this.durationTimeLabel.html(this.calculateTime(this.duration));
    }

    changeCurrentTime(time) {
        this.currentTimeLabel.html(this.calculateTime(time));

        this.audioEl.element.currentTime = +time;
    }

    getCurrentTime() {
        return this.audioEl.prop('currentTime');
    }

    audioEndCheck() {
        return this.getCurrentTime() >= this.duration;
    }

    setSeekBeforeWidth(percent) {
        this.css(
            '--seek-before-width',
            percent + '%',
        );
    }

    showPointer() {
        this.currentProgress.addClass('pointer-show');
    }

    hidePointer() {
        this.currentProgress.removeClass('pointer-show');
    }
}
